import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../../shared/ui/kit/utils';
import { MESSAGE_KIND_CONFIG } from '../constants';
import { formatMessageTime } from '../utils';
import type { Message } from '../types';

interface MessageDetailProps {
    message: Message | null;
    onClose: () => void;
    onMarkAsRead: () => void;
    onArchive: () => void;
}

export const MessageDetail: React.FC<MessageDetailProps> = ({
    message,
    onClose,
    onMarkAsRead,
    onArchive,
}) => {
    if (!message) return null;

    const config = MESSAGE_KIND_CONFIG[message.kind];

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    onClick={(e) => e.stopPropagation()}
                    className={cn(
                        'w-full max-w-lg max-h-[90vh] overflow-auto',
                        'bg-white dark:bg-gray-900',
                        'rounded-2xl shadow-2xl',
                        'border border-gray-200 dark:border-gray-700'
                    )}
                >
                    <div className={cn(
                        'px-6 py-4 border-b border-gray-100 dark:border-gray-800',
                        config.bgColor
                    )}>
                        <div className="flex items-start gap-3">
                            <div className={cn(
                                'w-10 h-10 rounded-xl flex items-center justify-center',
                                'bg-white/50 dark:bg-black/20',
                                config.color
                            )}>
                                {config.icon === 'alert-circle' && (
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                )}
                            </div>
                            <div className="flex-1">
                                <h2 className="text-[16px] font-semibold text-gray-900 dark:text-gray-100">
                                    {message.title}
                                </h2>
                                <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    {formatMessageTime(message.timestamp)}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                            >
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        <p className="text-[14px] text-gray-700 dark:text-gray-300 leading-relaxed">
                            {message.body}
                        </p>

                        {message.metadata && (
                            <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4 space-y-2">
                                {message.metadata.siteName && (
                            <div className="flex items-center justify-between text-[13px]">
                                <span className="text-gray-500 dark:text-gray-400">Сайт</span>
                                <span className="font-mono text-gray-900 dark:text-gray-100">{message.metadata.siteName}</span>
                            </div>
                                )}
                                {message.metadata.duration && (
                                    <div className="flex items-center justify-between text-[13px]">
                                        <span className="text-gray-500 dark:text-gray-400">Длительность</span>
                                        <span className="font-mono text-gray-900 dark:text-gray-100">{message.metadata.duration} мин</span>
                                    </div>
                                )}
                                {message.metadata.resolvedAt && (
                                    <div className="flex items-center justify-between text-[13px]">
                                        <span className="text-gray-500 dark:text-gray-400">Восстановлено</span>
                                        <span className="font-mono text-emerald-600 dark:text-emerald-400">
                                            {formatMessageTime(message.metadata.resolvedAt)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex gap-3">
                        {message.status === 'unread' && (
                            <button
                                onClick={onMarkAsRead}
                                className={cn(
                                    'flex-1 py-2.5 px-4 rounded-xl text-[13px] font-medium',
                                    'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900',
                                    'hover:bg-gray-800 dark:hover:bg-gray-200',
                                    'transition-colors'
                                )}
                            >
                                Отметить прочитанным
                            </button>
                        )}
                        <button
                            onClick={onArchive}
                            className={cn(
                                'py-2.5 px-4 rounded-xl text-[13px] font-medium',
                                'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300',
                                'hover:bg-gray-200 dark:hover:bg-white/10',
                                'transition-colors'
                            )}
                        >
                            В архив
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};